import React from 'react';
import LegalPageLayout from './LegalPageLayout';

const CookiePolicyPage: React.FC = () => (
  <LegalPageLayout title="Cookie Policy" lastUpdated="June 2026">
    <p>
      This Cookie Policy explains how SkipScreen LLC (&quot;SkipScreen,&quot; &quot;we,&quot;
      &quot;our,&quot; or &quot;us&quot;) uses cookies and similar technologies when you visit or
      use our website, applications, and services.
    </p>
    <p>
      By using SkipScreen, you agree to the use of cookies and similar technologies as described in
      this Policy.
    </p>

    <h2>1. What Are Cookies?</h2>
    <p>Cookies are small text files stored on your device when you visit a website.</p>
    <p>Cookies help websites:</p>
    <ul>
      <li>Remember user preferences</li>
      <li>Maintain login sessions</li>
      <li>Improve website performance</li>
      <li>Understand how users interact with the platform</li>
      <li>Enhance security</li>
    </ul>
    <p>
      Cookies do not typically contain information that directly identifies you, but they may be
      linked to information associated with your account.
    </p>

    <h2>2. How SkipScreen Uses Cookies</h2>
    <p>SkipScreen uses cookies and similar technologies to:</p>
    <ul>
      <li>Keep users signed into their accounts</li>
      <li>Remember user preferences and settings</li>
      <li>Improve platform functionality and performance</li>
      <li>Protect against fraud, abuse, and unauthorized access</li>
      <li>Measure platform usage and engagement</li>
      <li>Improve user experience</li>
      <li>Support analytics and product development</li>
    </ul>

    <h2>3. Types of Cookies We Use</h2>

    <h3>Essential Cookies</h3>
    <p>These cookies are necessary for SkipScreen to operate properly.</p>
    <p>Examples include:</p>
    <ul>
      <li>Authentication and login management</li>
      <li>Security and fraud prevention</li>
      <li>Session management</li>
      <li>Account functionality</li>
    </ul>
    <p>Disabling these cookies may prevent parts of the platform from functioning properly.</p>

    <h3>Performance and Analytics Cookies</h3>
    <p>These cookies help us understand how visitors interact with SkipScreen.</p>
    <p>Examples include:</p>
    <ul>
      <li>Page visits</li>
      <li>Feature usage</li>
      <li>Session duration</li>
      <li>Error tracking</li>
      <li>Traffic sources</li>
    </ul>
    <p>This information helps us improve the platform and user experience.</p>

    <h3>Functional Cookies</h3>
    <p>These cookies remember choices you make while using SkipScreen.</p>
    <p>Examples include:</p>
    <ul>
      <li>Language preferences</li>
      <li>Display settings</li>
      <li>User preferences</li>
      <li>Saved selections</li>
    </ul>

    <h3>Security Cookies</h3>
    <p>Security-related cookies help protect user accounts and platform integrity.</p>
    <p>Examples include:</p>
    <ul>
      <li>Login protection</li>
      <li>Suspicious activity detection</li>
      <li>Abuse prevention</li>
      <li>Bot detection</li>
    </ul>

    <h2>4. Third-Party Services</h2>
    <p>
      SkipScreen may use trusted third-party services that place cookies or similar technologies.
    </p>
    <p>Examples may include:</p>
    <ul>
      <li>Google Analytics</li>
      <li>Google Sign-In</li>
      <li>LinkedIn Sign-In</li>
      <li>Cloudflare</li>
      <li>AWS services</li>
      <li>Customer support and communication tools</li>
      <li>Advertising and marketing platforms</li>
    </ul>
    <p>
      These third parties may collect information according to their own privacy policies.
      SkipScreen does not control third-party cookie practices.
    </p>

    <h2>5. Marketing and Advertising Cookies</h2>
    <p>SkipScreen may use cookies to:</p>
    <ul>
      <li>Measure marketing performance</li>
      <li>Understand advertising effectiveness</li>
      <li>Improve audience targeting</li>
      <li>Track campaign conversions</li>
    </ul>
    <p>
      These cookies may be placed by SkipScreen or approved third-party providers. Users may opt out
      of certain advertising cookies through browser settings or applicable privacy controls.
    </p>

    <h2>6. Managing Cookies</h2>
    <p>Most web browsers allow users to:</p>
    <ul>
      <li>View stored cookies</li>
      <li>Delete cookies</li>
      <li>Block cookies</li>
      <li>Restrict certain categories of cookies</li>
    </ul>
    <p>Please note that disabling certain cookies may affect functionality and user experience.</p>

    <h2>7. Do Not Track Signals</h2>
    <p>Some browsers offer a &quot;Do Not Track&quot; feature.</p>
    <p>
      Because there is currently no universally accepted standard for responding to such signals,
      SkipScreen may not respond to Do Not Track requests.
    </p>

    <h2>8. Changes to This Cookie Policy</h2>
    <p>SkipScreen may update this Cookie Policy from time to time.</p>
    <p>
      Changes become effective when posted on the platform. Continued use of SkipScreen after
      updates constitutes acceptance of the revised Policy.
    </p>

    <div className="legal-page__contact">
      <h2>9. Contact Us</h2>
      <p>
        If you have questions regarding this Cookie Policy, please contact:{' '}
        <a href="mailto:contact@useskipscreen.com">contact@useskipscreen.com</a>
      </p>
      <p>SkipScreen LLC</p>
      <p>© 2026 SkipScreen LLC. All rights reserved.</p>
    </div>
  </LegalPageLayout>
);

export default CookiePolicyPage;
